"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseImageOperationDocument = exports.parseImageOperationCommand = exports.parseCaptureStatusDocument = void 0;
const paths_1 = require("./paths");
const values_1 = require("./values");
const parsers_1 = require("./parsers");
const record = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error('Invalid image command.');
    return value;
};
const exact = (value, fields) => {
    if (Object.keys(value).some((key) => !fields.includes(key)))
        throw new Error('Unsupported image command field.');
};
const text = (value) => {
    if (typeof value !== 'string' || !value || value.length > 1500)
        throw new Error('Invalid image command identity.');
    return value;
};
const version = (value) => {
    if (!Number.isSafeInteger(value) || Number(value) < 0)
        throw new Error('Invalid membership version.');
    return Number(value);
};
const stamp = (value) => {
    const input = record(value);
    if (!Number.isSafeInteger(input.seconds) ||
        !Number.isInteger(input.nanoseconds) ||
        Number(input.nanoseconds) < 0 ||
        Number(input.nanoseconds) >= 1e9)
        throw new Error('Invalid image operation timestamp.');
    return {
        seconds: Number(input.seconds),
        nanoseconds: Number(input.nanoseconds),
    };
};
const parseCaptureStatusDocument = (value) => {
    const input = record(value);
    exact(input, [
        'schemaVersion',
        'revision',
        'actorUserId',
        'collectionId',
        'operationId',
        'expectedMembershipVersion',
        'state',
        'mode',
        'itemId',
        'analysisComplete',
        'errorCode',
        'aiSettlement',
        'updatedAt',
    ]);
    const actorUserId = text(input.actorUserId);
    const collectionId = text(input.collectionId);
    const operationId = (0, paths_1.requireOperationId)(text(input.operationId));
    paths_1.firestorePaths.member(collectionId, actorUserId);
    paths_1.firestorePaths.captureStatus(collectionId, operationId);
    if (input.schemaVersion !== 1 ||
        ![
            'uploading',
            'queued',
            'processing',
            'failed',
            'complete',
            'discarded',
        ].includes(String(input.state)) ||
        !['analysis', 'save'].includes(String(input.mode)))
        throw new Error('Invalid capture status.');
    const complete = input.state === 'complete';
    if (complete) {
        paths_1.firestorePaths.item(collectionId, text(input.itemId));
        if (typeof input.analysisComplete !== 'boolean')
            throw new Error('Invalid completion receipt.');
    }
    else if (input.itemId !== undefined ||
        input.analysisComplete !== undefined) {
        throw new Error('Unfinished captures cannot expose an item receipt.');
    }
    if ((input.errorCode !== undefined || input.aiSettlement !== undefined) &&
        input.state !== 'failed')
        throw new Error('Only failed captures have failure metadata.');
    if (input.errorCode !== undefined &&
        (typeof input.errorCode !== 'string' ||
            !/^[a-z][a-z0-9-]{0,79}$/.test(input.errorCode)))
        throw new Error('Invalid capture error code.');
    if (input.aiSettlement !== undefined &&
        !['refunded', 'reconciling'].includes(String(input.aiSettlement)))
        throw new Error('Invalid capture settlement.');
    return {
        schemaVersion: 1,
        revision: version(input.revision),
        actorUserId,
        collectionId,
        operationId,
        expectedMembershipVersion: version(input.expectedMembershipVersion),
        state: input.state,
        mode: input.mode,
        ...(complete
            ? {
                itemId: text(input.itemId),
                analysisComplete: input.analysisComplete,
            }
            : {}),
        ...(input.errorCode === undefined
            ? {}
            : { errorCode: input.errorCode }),
        ...(input.aiSettlement === undefined
            ? {}
            : {
                aiSettlement: input.aiSettlement,
            }),
        updatedAt: stamp(input.updatedAt),
    };
};
exports.parseCaptureStatusDocument = parseCaptureStatusDocument;
const parseImageOperationCommand = (value) => {
    const input = record(value);
    const collectionId = text(input.collectionId);
    paths_1.firestorePaths.collection(collectionId);
    const operationId = (0, paths_1.requireOperationId)(text(input.operationId));
    const identity = {
        collectionId,
        operationId,
        ...(input.expectedMembershipVersion === undefined
            ? {}
            : {
                expectedMembershipVersion: version(input.expectedMembershipVersion),
            }),
    };
    const common = [
        'action',
        'collectionId',
        'operationId',
        'expectedMembershipVersion',
    ];
    if (input.action === 'collection-ai') {
        const { action, ...request } = input;
        const parsed = (0, parsers_1.parseRunCollectionAiRequest)(request);
        return { action, ...parsed };
    }
    if (input.action === 'capture-submit' || input.action === 'capture-control') {
        const expectedMembershipVersion = version(input.expectedMembershipVersion);
        const analysisOperationId = input.analysisOperationId === undefined
            ? undefined
            : (0, paths_1.requireOperationId)(text(input.analysisOperationId));
        const acceptedAiCostVersion = input.acceptedAiCostVersion === undefined
            ? undefined
            : text(input.acceptedAiCostVersion);
        if (Boolean(analysisOperationId) !== Boolean(acceptedAiCostVersion) ||
            analysisOperationId === operationId) {
            throw new Error('Capture AI identity and consent must be supplied together.');
        }
        const ai = analysisOperationId
            ? { analysisOperationId, acceptedAiCostVersion }
            : {};
        if (input.action === 'capture-control') {
            exact(input, [
                ...common,
                'phase',
                'analysisOperationId',
                'acceptedAiCostVersion',
            ]);
            if (!['status', 'retry', 'save', 'discard'].includes(String(input.phase)))
                throw new Error('Invalid capture control.');
            if (input.phase !== 'retry' && analysisOperationId)
                throw new Error('Only retry may change AI consent.');
            return {
                ...identity,
                action: 'capture-control',
                expectedMembershipVersion,
                phase: input.phase,
                ...ai,
            };
        }
        exact(input, [
            ...common,
            'itemId',
            'fields',
            'analysisOperationId',
            'acceptedAiCostVersion',
        ]);
        const itemId = text(input.itemId);
        paths_1.firestorePaths.item(collectionId, itemId);
        exact(record(input.fields), [
            'keywords',
            'location',
            'locationId',
            'qty',
            'tagIds',
        ]);
        const parsed = (0, parsers_1.parseCreateCollectionItemRequest)({
            collectionId,
            operationId,
            item: input.fields,
        });
        return {
            ...identity,
            action: 'capture-submit',
            expectedMembershipVersion,
            itemId,
            fields: parsed.item,
            ...ai,
        };
    }
    const shareId = input.shareId === undefined
        ? undefined
        : (0, paths_1.requireOperationId)(text(input.shareId));
    if (shareId && input.expectedMembershipVersion !== undefined)
        throw new Error('Use one authority scope.');
    const linkToken = input.linkToken === undefined ? undefined : text(input.linkToken);
    if (linkToken && (!shareId || linkToken.length > 300))
        throw new Error('Invalid link credential.');
    const share = shareId ? { shareId, ...(linkToken ? { linkToken } : {}) } : {};
    const fields = record(input.fields || {});
    exact(fields, ['keywords', 'location', 'locationId', 'qty', 'tagIds']);
    if (input.action === 'create-item') {
        exact(input, [
            'action',
            'collectionId',
            'operationId',
            'fields',
            'sourceItemId',
            'shareId',
            'linkToken',
        ]);
        const sourceItemId = input.sourceItemId === undefined ? undefined : text(input.sourceItemId);
        if (sourceItemId)
            paths_1.firestorePaths.item(collectionId, sourceItemId);
        const parsed = (0, parsers_1.parseCreateCollectionItemRequest)({
            collectionId,
            operationId,
            item: fields,
        });
        return {
            ...identity,
            ...share,
            action: 'create-item',
            fields: parsed.item,
            ...(sourceItemId ? { sourceItemId } : {}),
        };
    }
    if (input.action === 'replace-image') {
        exact(input, [
            ...common,
            'itemIds',
            'fields',
            'shareId',
            'linkToken',
            'expectedRevisions',
        ]);
        if (!Array.isArray(input.itemIds) ||
            !input.itemIds.length ||
            input.itemIds.length > 400)
            throw new Error('Select 1–400 items.');
        const itemIds = [...new Set(input.itemIds.map(text))].sort();
        let expectedRevisions;
        if (shareId || input.expectedRevisions !== undefined) {
            const revisions = record(input.expectedRevisions);
            exact(revisions, itemIds);
            expectedRevisions = Object.fromEntries(itemIds.map((id) => [id, version(revisions[id])]));
        }
        if (fields.tagIds !== undefined && !expectedRevisions)
            throw new Error('Tag changes require item revisions.');
        if (Object.keys(fields).length && itemIds.length !== 1)
            throw new Error('Field edits require one item.');
        const parsed = (0, parsers_1.parseCollectionBulkUpdateRequest)({
            collectionId,
            operationId,
            items: itemIds.map((id) => ({ id, updates: { ...fields, image: '' } })),
        });
        const { image: _image, ...normalized } = parsed.items[0].updates;
        return {
            ...identity,
            ...share,
            action: 'replace-image',
            itemIds,
            ...(expectedRevisions ? { expectedRevisions } : {}),
            ...(Object.keys(normalized).length ? { fields: normalized } : {}),
        };
    }
    throw new Error('Unsupported image operation.');
};
exports.parseImageOperationCommand = parseImageOperationCommand;
const durableSourcePath = (collectionId, value) => {
    const path = text(value);
    const prefix = `collections/${collectionId}/images/`;
    if (!path.startsWith(prefix) ||
        paths_1.storagePaths.durableImage(collectionId, path.slice(prefix.length)) !== path) {
        throw new Error('Invalid image operation source.');
    }
    return path;
};
const parseImageOperationDocument = (value) => {
    const input = record(value);
    exact(input, [
        'schemaVersion',
        'revision',
        'actorUserId',
        'collectionId',
        'operationId',
        'action',
        'canonicalPayloadHash',
        'payloadHashVersion',
        'objectPath',
        'sourceStoragePath',
        'itemId',
        'state',
        'leaseId',
        'leaseExpiresAt',
        'createdAt',
        'updatedAt',
        'result',
    ]);
    const collectionId = text(input.collectionId);
    const operationId = (0, paths_1.requireOperationId)(text(input.operationId));
    paths_1.firestorePaths.imageOperation(collectionId, operationId);
    const actorUserId = text(input.actorUserId);
    paths_1.firestorePaths.member(collectionId, actorUserId);
    if (input.schemaVersion !== 1 ||
        !['pending', 'complete'].includes(text(input.state)) ||
        !['create-item', 'replace-image'].includes(text(input.action)) ||
        input.payloadHashVersion !== values_1.OPERATION_PAYLOAD_HASH_VERSION)
        throw new Error('Invalid image operation state.');
    let objectPath;
    if (input.objectPath !== undefined) {
        objectPath = text(input.objectPath);
        if (objectPath !==
            paths_1.storagePaths.durableImage(collectionId, `${operationId}.jpg`))
            throw new Error('Invalid image operation object.');
    }
    const sourceStoragePath = input.sourceStoragePath === undefined
        ? undefined
        : durableSourcePath(collectionId, input.sourceStoragePath);
    if (sourceStoragePath && (input.action !== 'create-item' || objectPath))
        throw new Error('Use one image operation source.');
    const itemId = input.itemId === undefined ? undefined : text(input.itemId);
    if (itemId)
        paths_1.firestorePaths.item(collectionId, itemId);
    if ((input.action === 'create-item' && !itemId) ||
        (input.action === 'replace-image' && (itemId || !objectPath)))
        throw new Error('Invalid image operation target.');
    let result;
    if (input.state === 'pending' && input.result !== undefined)
        throw new Error('Pending operation cannot have a result.');
    if (input.state === 'complete') {
        const output = record(input.result);
        exact(output, [
            'operationId',
            'itemId',
            'itemsUpdated',
            'storagePath',
            'idempotent',
        ]);
        if ((0, paths_1.requireOperationId)(text(output.operationId)) !== operationId ||
            typeof output.idempotent !== 'boolean') {
            throw new Error('Invalid image operation result identity.');
        }
        if (input.action === 'create-item') {
            if (output.itemId !== itemId || output.itemsUpdated !== undefined)
                throw new Error('Invalid image creation result.');
            if (output.storagePath !== undefined &&
                output.storagePath !== (objectPath || sourceStoragePath)) {
                throw new Error('Invalid image creation result source.');
            }
            result = {
                operationId,
                itemId,
                idempotent: output.idempotent,
                ...(output.storagePath === undefined
                    ? {}
                    : { storagePath: text(output.storagePath) }),
            };
        }
        else {
            const itemsUpdated = version(output.itemsUpdated);
            if (itemsUpdated < 1 ||
                itemsUpdated > 400 ||
                output.itemId !== undefined ||
                output.storagePath !== objectPath) {
                throw new Error('Invalid image replacement result.');
            }
            result = {
                operationId,
                itemsUpdated,
                storagePath: objectPath,
                idempotent: output.idempotent,
            };
        }
    }
    return {
        schemaVersion: 1,
        revision: version(input.revision),
        collectionId,
        operationId,
        actorUserId,
        action: input.action,
        state: input.state,
        canonicalPayloadHash: (0, paths_1.requireSha256)(text(input.canonicalPayloadHash)),
        payloadHashVersion: values_1.OPERATION_PAYLOAD_HASH_VERSION,
        leaseId: (0, paths_1.requireOperationId)(text(input.leaseId)),
        leaseExpiresAt: stamp(input.leaseExpiresAt),
        createdAt: stamp(input.createdAt),
        updatedAt: stamp(input.updatedAt),
        ...(objectPath ? { objectPath } : {}),
        ...(sourceStoragePath ? { sourceStoragePath } : {}),
        ...(itemId ? { itemId } : {}),
        ...(result ? { result } : {}),
    };
};
exports.parseImageOperationDocument = parseImageOperationDocument;
